import React from 'react';
import SEO from '../components/SEO';
import { PageHero } from '../components/marketing';

const SECTIONS = [
  {
    title: '1. Who We Are',
    body: (
      <>
        <p>
          True Global Education ("True Global", "we", "us" or "our") is an education-consulting and student-recruitment
          firm headquartered at 8166 128 St #109, Surrey, BC V3W 4G1, Canada. We help prospective students plan careers,
          prepare for admission tests, apply to colleges and universities, secure student aid, and obtain study-permit
          visas for Canada, the United States, the United Kingdom, Australia, Ireland, Germany and other destinations.
        </p>
        <p>
          This Privacy Policy explains what personal information we collect, why we collect it, how we use and protect
          it, and the choices you have. It applies to <strong>trueglobaleducation.com</strong>, our recruiter and student
          portals, our advertising on Meta (Facebook, Instagram), Google, TikTok and similar networks, and any
          communications we send by email, SMS, WhatsApp or voice call.
        </p>
      </>
    ),
  },
  {
    title: '2. Information We Collect',
    body: (
      <>
        <p>We collect personal information in three ways:</p>
        <p><strong>(a) Information you give us directly.</strong> When you complete a lead form, request a consultation,
        sign up as a student or recruiter, upload identification or academic documents, or contact our team, we
        collect: full name, date of birth, gender, nationality, country of residence, mailing address, email address,
        phone number, parent/guardian details, academic history and transcripts, test scores (IELTS, TOEFL, SAT, GRE,
        GMAT, PTE, Duolingo, etc.), passport details, study-permit history, intended programs and countries, budget,
        recruiter affiliation, payment details, signed agreements and any other information you choose to share.</p>
        <p><strong>(b) Information collected from advertising and third-party platforms.</strong> When you click one of
        our Facebook, Instagram, Google or TikTok lead ads and submit the embedded form, the platform shares the form
        answers (name, email, phone, city, etc.), the ad/campaign ID, the form ID and a unique lead ID with us through
        their official advertising APIs. We may also receive information from referrer recruiters who introduce you to
        us.</p>
        <p><strong>(c) Information collected automatically.</strong> We use first- and third-party cookies, pixels and
        analytics tools (Meta Pixel, Google Analytics, Microsoft Clarity, etc.) to record your IP address, device type,
        browser, operating system, pages visited, time spent, scroll depth, button clicks and approximate location.
        Our admin and recruiter portals also log activity such as login timestamps, document downloads and changes you
        make to records.</p>
      </>
    ),
  },
  {
    title: '3. How We Use Your Information',
    body: (
      <>
        <p>We use personal information to:</p>
        <ul>
          <li>respond to your inquiries and provide the consulting, application, financial-aid and visa services you
          request;</li>
          <li>match you with suitable colleges, universities, scholarship programs and recruiter partners;</li>
          <li>contact you by email, SMS, WhatsApp or automated/AI voice call with consultation reminders, application
          updates, document requests, marketing offers and event invitations;</li>
          <li>process payments and pay recruiter commissions;</li>
          <li>verify the authenticity of academic, financial and identity documents you submit;</li>
          <li>improve our website, advertising and services through aggregated analytics and AI-assisted lead scoring;</li>
          <li>comply with legal, regulatory and audit obligations including Canadian immigration consultant (ICCRC /
          CICC) record-keeping rules; and</li>
          <li>protect against fraud, abuse and unauthorised access.</li>
        </ul>
        <p>We do not sell your personal information. We do not use your information for any purpose that is not
        compatible with the purpose for which it was collected, unless you give us new consent.</p>
      </>
    ),
  },
  {
    title: '4. Legal Basis for Processing',
    body: (
      <>
        <p>Depending on where you live, the legal basis for our processing is one or more of the following: (i) your
        consent, which you may withdraw at any time; (ii) the performance of a contract you have with us (such as a
        signed service agreement or recruiter agreement); (iii) compliance with a legal obligation; or (iv) our
        legitimate interest in operating, securing and growing our business in a way that does not override your
        rights.</p>
      </>
    ),
  },
  {
    title: '5. How We Share Your Information',
    body: (
      <>
        <p>We share personal information only with the parties that need it to deliver the services you have asked
        for, and only to the extent required:</p>
        <ul>
          <li><strong>Partner colleges, universities, language-test centres and scholarship providers</strong> — to
          submit your applications on your behalf.</li>
          <li><strong>Licensed immigration consultants and visa-processing partners</strong> — to prepare and file
          study-permit applications.</li>
          <li><strong>Recruiter network</strong> — your referring recruiter receives application status updates and
          commission-tracking information about students they introduce.</li>
          <li><strong>Service providers</strong> we contract with under written confidentiality terms, including:
          Cloudflare R2 (document storage), MongoDB Atlas (database), Resend (transactional email), Twilio (SMS,
          WhatsApp and voice), DocuSign (e-signatures), Meta Platforms (advertising and lead-ad delivery), Google
          (advertising and analytics) and AI providers (OpenAI, Anthropic, Google Gemini) used for lead scoring,
          translation and conversation summarisation.</li>
          <li><strong>Regulators, auditors and law-enforcement bodies</strong> when required by law or to protect our
          legal rights.</li>
          <li><strong>Successors</strong> in the event of a merger, acquisition, financing or sale of all or part of
          our business.</li>
        </ul>
      </>
    ),
  },
  {
    title: '6. International Transfers',
    body: (
      <>
        <p>Our servers and many of our service providers are based outside Canada, including in the United States and
        the European Union. When we transfer your information internationally we rely on lawful transfer mechanisms
        (such as Standard Contractual Clauses) and require the recipient to apply protections at least equivalent to
        those required under Canadian law.</p>
      </>
    ),
  },
  {
    title: '7. How Long We Keep Information',
    body: (
      <>
        <p>We retain personal information for as long as is necessary to provide our services, comply with our legal
        and audit obligations, resolve disputes and enforce our agreements. In practice this means most student-file
        information is kept for 7 years from the close of your file, after which it is permanently deleted or
        irreversibly anonymised. Marketing-only leads who never become clients are deleted within 24 months of last
        contact unless you ask us to delete them sooner.</p>
      </>
    ),
  },
  {
    title: '8. Your Rights',
    body: (
      <>
        <p>Subject to applicable law you have the right to:</p>
        <ul>
          <li>access the personal information we hold about you;</li>
          <li>correct or update inaccurate information;</li>
          <li>request deletion ("right to be forgotten");</li>
          <li>withdraw consent to marketing communications at any time (every SMS, WhatsApp and email contains an
          opt-out; you can also reply STOP);</li>
          <li>object to or restrict certain processing;</li>
          <li>request a portable copy of the information you provided to us; and</li>
          <li>lodge a complaint with the Office of the Privacy Commissioner of Canada or your local data-protection
          authority.</li>
        </ul>
        <p>To exercise any of these rights, email <a href="mailto:info@trueglobaleducation.com" className="text-tge-navy underline">info@trueglobaleducation.com</a> from the address on file. We respond within 30 days.</p>
      </>
    ),
  },
  {
    title: '9. Security',
    body: (
      <>
        <p>We use TLS encryption in transit, AES-256 encryption at rest for document storage, role-based access
        control, JWT-based authentication, password hashing with bcrypt, audit logging and regular security reviews
        to protect your information. No method of transmission or storage is 100% secure; we cannot guarantee
        absolute security but we work hard to minimise risk.</p>
      </>
    ),
  },
  {
    title: '10. Children',
    body: (
      <>
        <p>Our services are intended for people aged 16 and above. Where a student is under 18 we require a
        parent or legal guardian to provide consent and co-sign all engagement agreements.</p>
      </>
    ),
  },
  {
    title: '11. Cookies & Tracking',
    body: (
      <>
        <p>You can control cookies through your browser settings and through the cookie preference banner shown on
        first visit. Disabling non-essential cookies will not stop core site functionality but may limit personalised
        content and ad performance reporting.</p>
      </>
    ),
  },
  {
    title: '12. Changes to This Policy',
    body: (
      <>
        <p>We may update this Policy from time to time. When we do, we will revise the "Last updated" date below and,
        if changes are material, notify you by email or with a prominent notice on the website.</p>
      </>
    ),
  },
  {
    title: '13. Contact Us',
    body: (
      <>
        <p>For privacy questions, requests or complaints, contact our Privacy Officer:</p>
        <p>
          True Global Education<br />
          8166 128 St #109, Surrey, BC V3W 4G1, Canada<br />
          Email: <a href="mailto:info@trueglobaleducation.com" className="text-tge-navy underline">info@trueglobaleducation.com</a><br />
          Phone: <a href="tel:6048141515" className="text-tge-navy underline">+1 604 814 1515</a>
        </p>
      </>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <div className="bg-tge-paper min-h-screen" data-testid="privacy-page">
      <SEO
        title="Privacy Policy — True Global Education"
        description="How True Global Education collects, uses, shares and protects personal information of students, recruiters and visitors."
      />
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        intro="Your trust is the foundation of everything we do. Here is exactly what we collect, how we use it, and the control you have over your information."
      />
      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-6 py-16 lg:py-20">
          <p className="text-sm text-tge-muted mb-10">
            <strong>Last updated:</strong> 28 May 2026
          </p>

          <div className="space-y-12 prose prose-tge max-w-none">
            {SECTIONS.map((s) => (
              <article key={s.title} data-testid={`privacy-section-${s.title.split('.')[0].trim()}`}>
                <h2 className="font-serif-display text-2xl sm:text-3xl text-tge-navy mb-4">
                  {s.title}
                </h2>
                <div className="text-tge-muted text-base leading-relaxed space-y-4 [&_a]:text-tge-navy [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_strong]:text-tge-navy">
                  {s.body}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
